'use client';
import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { useAuth } from '@/lib/AuthContext';

const AUTHORIZED_EMAIL = 'emailajefferson@gmail.com';

interface AppLayoutProps {
  children: React.ReactNode;
  currentPath?: string;
}

export default function AppLayout({ children, currentPath = '/' }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { user, loading, session } = useAuth();
  const hasRedirected = useRef(false);
  const [currentWindowPath, setCurrentWindowPath] = useState('');

  useEffect(() => {
    setCurrentWindowPath(window.location.pathname);
  }, []);

  // Derive auth state only after loading is complete
  const sessionExists = !loading && !!session;
  const userEmail = user?.email ?? null;
  const isAuthorized = userEmail?.toLowerCase() === AUTHORIZED_EMAIL.toLowerCase();

  const appLayoutDecision = loading
    ? 'waiting — loading'
    : !sessionExists
    ? 'no session → redirect to login'
    : !isAuthorized
    ? 'unauthorized email → redirect to access-denied' :'authorized → render dashboard';

  useEffect(() => {
    // Wait until loading is fully resolved before making any redirect decision
    if (loading) return;
    // Only redirect once per mount
    if (hasRedirected.current) return;
    // If session exists and user is authorized — NEVER redirect
    if (session && user && user.email?.toLowerCase() === AUTHORIZED_EMAIL.toLowerCase()) return;

    if (!session || !user) {
      hasRedirected.current = true;
      // DO NOT clear localStorage or sessionStorage — Supabase stores the session token there
      window.location.replace('/sign-up-login-screen');
      return;
    }

    if (user.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
      hasRedirected.current = true;
      window.location.replace('/access-denied');
    }
  }, [loading, session, user]);

  // Still loading — show spinner, never redirect
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Verifying session…</p>
        </div>
      </div>
    );
  }

  // Session exists and authorized — render dashboard immediately
  if (sessionExists && isAuthorized) {
    return (
      <div className="flex h-screen overflow-hidden bg-background">
        {/* Mobile overlay */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-40 lg:hidden animate-fade-in"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <Sidebar
          collapsed={sidebarCollapsed}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
          currentPath={currentPath}
        />

        {/* Main content area */}
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <Topbar
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
            onMobileMenuOpen={() => setMobileSidebarOpen(true)}
            sidebarCollapsed={sidebarCollapsed}
          />
          <main className="flex-1 overflow-y-auto scrollbar-thin bg-background">
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 xl:px-8 2xl:px-10 py-6">
              {children}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Not authenticated or not authorized — show redirecting message (redirect fires in useEffect)
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
      <p className="text-sm text-muted-foreground">Redirecting…</p>
    </div>
  );
}